import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { loadOneBitIcons, shuffled } from '../../utils/oneBit';
import { fetchCustomOneBitIcons } from '../../services/oneBitCustomService';
import { useInfiniteList } from './useInfiniteList';

const THUMB = 48;

// A bitmap drawn crisp at the largest whole-number scale that fits the box.
export function BitmapThumb({ bitmap, box = THUMB }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const scale = Math.max(1, Math.floor(box / Math.max(bitmap.w, bitmap.h)));
    canvas.width = bitmap.w * scale;
    canvas.height = bitmap.h * scale;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#16181d';
    bitmap.bits.forEach((v, i) => v && ctx.fillRect((i % bitmap.w) * scale, Math.floor(i / bitmap.w) * scale, scale, scale));
  }, [bitmap, box]);
  return <canvas ref={ref} className="nn-bit-thumb" />;
}

// Every 1-bit icon from all packs in one random order (new on every open); more load while scrolling.
function OneBitPickerDialog({ onSelect, onClose }) {
  const [icons, setIcons] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    // Icons added from sprite sheets; the picker still works if they can't be read.
    Promise.all([loadOneBitIcons(), fetchCustomOneBitIcons().catch(() => [])])
      .then(([library, custom]) => setIcons(shuffled([...library, ...custom])))
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return icons ? icons.filter(i => !q || (i.name || '').toLowerCase().includes(q)) : [];
  }, [icons, query]);
  const { count, hasMore, sentinel } = useInfiniteList(matches.length, 160, query);

  return ReactDOM.createPortal(
    <div className="emoji-overlay" onClick={onClose}>
      <div className="emoji-dialog nn-icon-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-label="Valitse 1-bittinen kuva">
        <div className="emoji-head">
          <input className="emoji-search" autoFocus placeholder={icons ? `Satunnaisia kuvia (${icons.length}), hae nimellä englanniksi...` : 'Ladataan...'}
            value={query} onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && matches[0] && onSelect(matches[0])} />
          <button type="button" className="emoji-close" onClick={onClose} aria-label="Sulje">
            <span className="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>
        <div className="nn-icon-grid nn-bit-grid">
          {error && <div className="emoji-note">{error}</div>}
          {icons && !matches.length && <div className="emoji-note">Ei kuvia. Vain osalla kuvista on nimi.</div>}
          {matches.slice(0, count).map((icon, i) => (
            <button key={i} type="button" className="nn-icon-btn nn-bit-btn" title={`${icon.name || ''} ${icon.w}×${icon.h}`.trim()} onClick={() => onSelect(icon)}>
              <BitmapThumb bitmap={icon} />
              <span className="nn-icon-name">{icon.w}×{icon.h}</span>
            </button>
          ))}
          {hasMore && <div ref={sentinel} className="nn-sentinel" />}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default OneBitPickerDialog;
